import { ExtranetProductShortageOrderDto } from '@pharmac-extranet/extranet-api'
import {
  ExtranetContainer,
  ExtranetTableColumn,
  ExtranetTableRow,
  formatDateString,
  useBreakpoint,
  useLanguage,
} from '@pharmac-extranet/shared'
import { PharmacIcon, PharmacText, PharmacTooltip } from '@pharmac/ui'
import { formatBirthdate } from '../../utils/formatters'
import { CatalogItemAvailability } from '../dose-dispensing-catalog/CatalogItemAvailability'
import { HoverColumn } from './ProductShortagesCustomerRow'

type Props = {
  order: ExtranetProductShortageOrderDto
}

export const ProductShortagesCustomerDetails = ({ order }: Props) => {
  const { t } = useLanguage(['extranet'])
  const { maxMobile } = useBreakpoint()

  return (
    <ExtranetContainer full>
      <ExtranetContainer
        flexColumn
        full
        paddingMediumHorizontal
        paddingMediumVertical
      >
        <PharmacText paddingSmallBottom>{t('orderHistory.dateOfBirth')}</PharmacText>
        <PharmacText
          weight="bold"
          paddingMediumBottom
        >
          {formatBirthdate(order.anonymizedSocialSecurityNumber)}
        </PharmacText>
        <PharmacText paddingSmallBottom>{t('orderHistory.subscriptionPeriod')}</PharmacText>

        <ExtranetContainer flexRow>
          <PharmacText
            weight="bold"
            paddingMediumBottom
          >
            {formatDateString(order.startDate)}-{formatDateString(order.endDate)}
          </PharmacText>
          <PharmacTooltip
            paddingSmallLeft
            maxWidth={maxMobile ? '280px' : '400px'}
            handle={
              <PharmacIcon
                style={{ color: 'var(--color-info)' }}
                size="20px"
                icon="info"
              />
            }
          >
            <PharmacText>{t('productShortages.subscriptionPeriodInfo')}</PharmacText>
          </PharmacTooltip>
        </ExtranetContainer>

        <PharmacText paddingMediumBottom>
          {t('medicines.availableForCustomers')} ({order.products.length})
        </PharmacText>
        {order.products.map((a) => (
          <ExtranetTableRow
            key={a.id}
            expand={
              <ExtranetContainer
                flexColumn
                full
                paddingMediumHorizontal
                paddingMediumVertical
                style={{
                  backgroundColor: 'var(--color-accent-1)',
                  borderTop: '4px solid var(--color-accent-3)',
                }}
              >
                <ExtranetContainer
                  flexRow
                  alignCenter
                >
                  <div style={{ fontWeight: 'bold' }}>
                    <CatalogItemAvailability availability={a.availableForCustomers} />
                  </div>
                </ExtranetContainer>
                <PharmacText
                  paddingMediumBottom
                  paddingLargeTop
                >
                  {t('medicines.substituteProduct')}:
                </PharmacText>
                <ExtranetContainer full>
                  <ExtranetTableRow
                    key={a.id}
                    children={
                      <>
                        <ExtranetTableColumn
                          size={maxMobile ? 0.7 : 0.3}
                          style={{
                            backgroundColor: 'var(--extranet-light-purple)',
                            justifyContent: 'center',
                          }}
                        >
                          <ExtranetContainer
                            flexRow
                            alignCenter
                          >
                            <PharmacIcon icon="refreshCcw" />
                          </ExtranetContainer>
                        </ExtranetTableColumn>
                        <HoverColumn
                          style={{ backgroundColor: 'var(--color-accent-1)' }}
                          size={maxMobile ? 3 : 4}
                        >
                          <ExtranetContainer flexRow>
                            <PharmacText
                              paddingMediumHorizontal={!maxMobile}
                              paddingSmallHorizontal={maxMobile}
                              style={{ textDecoration: 'underline' }}
                            >
                              {a.substituteProduct?.productNumber}
                            </PharmacText>
                            <PharmacText style={{ textDecoration: 'underline' }}>
                              {a.substituteProduct?.nameFi} {a.substituteProduct?.strength}
                            </PharmacText>
                          </ExtranetContainer>
                        </HoverColumn>
                      </>
                    }
                  />
                </ExtranetContainer>
              </ExtranetContainer>
            }
            isExpanded
            toggleExpand={() => null}
            children={
              <>
                <ExtranetTableColumn
                  size={maxMobile ? 1 : 0.5}
                  style={{
                    backgroundColor: 'var(--extranet-light-purple)',
                    justifyContent: 'center',
                  }}
                >
                  <PharmacText weight="bold">{a.productNumber}</PharmacText>
                </ExtranetTableColumn>
                <HoverColumn
                  style={{ backgroundColor: 'var(--color-accent-1)' }}
                  marginMediumLeft={!maxMobile}
                  size={maxMobile ? 3 : 4}
                >
                  <PharmacText weight="bold">
                    {a.nameFi} {a.strength}
                  </PharmacText>
                </HoverColumn>
              </>
            }
          />
        ))}
      </ExtranetContainer>
    </ExtranetContainer>
  )
}
